import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { useToast } from '@/storybook/components/Toast/Toast';
import { readCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import collectAllLocalFilters from '@/utils/filter/collectAllLocalFilters';
import { savedFiltersEvents } from './savedFiltersEvents';
import fetchAndApplyFilter from '@/utils/filter/fetchAndApplyFilter';

interface SaveFiltersParams {
  filterName: string;
  isDefault: boolean;
}

interface UseSaveFiltersOptions {
  onSuccess?: () => void;
}

interface UseSaveFiltersResult {
  handleSaveFilters: (params: SaveFiltersParams) => Promise<boolean>;
  isLoading: boolean;
}

const useSaveFilters = ({ onSuccess }: UseSaveFiltersOptions = {}): UseSaveFiltersResult => {
  const { t } = useTranslation();
  const { filterState, handleApplyFilter, filterConfig, optionPathsByFilter, replaceOptionPaths } =
    useContext(FiltersContext);
  const { userInfo } = useAuthContext();
  const { makeToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const savingRef = useRef(false);

  const handleSaveFilters = useCallback(
    async ({ filterName, isDefault }: SaveFiltersParams): Promise<boolean> => {
      if (savingRef.current) return false;
      savingRef.current = true;
      setIsLoading(true);
      try {
        const localFilters = collectAllLocalFilters();
        const hasLocalFilters = Object.keys(localFilters).length > 0;
        const resolvedCountryCodes = readCachedCountryCodes();
        const optionPaths = optionPathsByFilter ?? {};
        const hasOptionPaths = Object.keys(optionPaths).length > 0;

        const payload: Record<string, unknown> = {
          globalFilters: filterState,
          ...(hasLocalFilters && { localFilters }),
          ...(resolvedCountryCodes.length > 0 && { resolvedCountryCodes }),
          ...(hasOptionPaths && { optionPaths }),
          senderCountryCodes: userInfo?.countries ?? [],
        };

        const response = await UserFilterApiService.createFilter({
          filterType: 'saved_filter',
          filterName,
          isDefault,
          filters: payload,
        });

        await fetchAndApplyFilter({
          filterId: response.data.uniqueFilterId,
          filterMetadata: {
            userFilterId: response.data.uniqueFilterId,
            filterName,
            filterType: response.data.filterType,
            filterCountries: response.data.filterCountries,
            filterDate: response.data.filterDate,
            isDefault,
          },
          filterConfig,
          handleApplyFilter,
          replaceOptionPaths,
        });

        makeToast({
          content: String(t('global.filters.saveFiltersDialog.saveSuccess')),
          appearance: 'success',
          autoClose: 5000,
        });

        onSuccess?.();
        savedFiltersEvents.notify();
        return true;
      } catch (error) {
        const apiMessage =
          axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
            ? error.response.data.message
            : undefined;
        makeToast({
          content: apiMessage ?? String(t('global.filters.saveFiltersDialog.saveError')),
          appearance: 'error',
        });
        return false;
      } finally {
        savingRef.current = false;
        setIsLoading(false);
      }
    },
    [
      filterState,
      filterConfig,
      handleApplyFilter,
      optionPathsByFilter,
      replaceOptionPaths,
      userInfo,
      makeToast,
      onSuccess,
      t,
    ],
  );

  return { handleSaveFilters, isLoading };
};

export default useSaveFilters;
